import React, { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import {
    StBackButton,
    StButtonWithSpace,
    StCheck,
    StCommentsButton,
    StListName,
    StListNameForm,
    StMoreButton,
    StNameInput,
    StPrivacyButton,
    StSelectionListHeader,
    StSelectionListIcon,
    StTotalDropDown
} from './StyledHeaderComponents';
import Utils from '../../../shared/utils';
import TotalAmountBadges from '../../../shared/common/TotalAmountBadges';
import {
    BOLINDA_ICON_BOLINDA_ONLY,
    MATERIAL_ICON_CART,
    MATERIAL_ICON_DOWNLOAD
} from '../../../shared/common/Button/Icon';
import ConfirmationModalLayout from '../../../shared/components/modal/ConfirmationModalLayout';
import SelectionBulkMenu from '../../../shared/components/SelectionBulkMenu';

import { ImportIsbnDialog } from './ImportIsbnDialog';
import SelectionBulkContainer from './SelectionBulkContainer';
import SendForApprovalDialog from './SendForApprovalDialog';
import { SelectionList } from '../../../../models/SelectionList';
import { PriceTier } from '../../../../models/PriceTier';
import { AuthenticationState, SpocState } from '../../../store/authentication/state';
import { ButtonSize } from '../../../shared/common/Button';
import {
    AnalyseResponseOut,
    BulkActionResponse,
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    SelectionListOut
} from '../../../store/api';
import AnalyseDropDown from '../../analyse/AnalyseDropDown';
import { SelectionListUpdateParams, useChangeVisibility } from './hooks/selectionListHooks';
import { ErrorResponse, globalWarning } from '../../../shared/notification/actions';
import { invalidateSelectionListSummary } from '../../../api/selection-list/useSelectionListSummary';
import { SubscriptionPeriodType } from '../../../../models/Article';
import { useTranslation } from 'react-i18next';
import { UseMutateFunction, UseMutationResult } from '@tanstack/react-query';
import { useCommentsBarContext } from '../CommentsProvider';
import { fetchSelectionListAnalyse } from '../../../api/selection-list/fetchAnalyse';
import AnalyseTable from '../../analyse/AnalyseTable';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import {
    CopyToCartOptions,
    IsbnAddToListOptions,
    isCopyToCartOptions,
    isIsbnAddToListOptions
} from '../../../../models/ListContentCheckOptions';
import { UnavailableProductsModal } from '../../../shared/common/UnavailableProductsModal';
import { ACTION_ADD, ACTION_COPY, ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../../store/constants';
import { TFunction } from 'i18next';
import { useAddByIsbnsToList } from '../../../api/selection-list/useAddByIsbnsToList';
import { MAX_SELECT } from '../../../shared/product-selection/useProductSelection';
import { ProductSelection } from '../../../shared/product-selection/ProductSelectionTypes';
import { getProductSelectionCount, nothingSelected } from '../../../shared/product-selection/ProductSelectionUtils';
import { useSendForApproval } from '../../../api/selection-list/useSendForApproval';
import i18n from '../../../../i18n';
import { useStickyHeader } from '../../../shared/header/useStickyHeader';
import { showNotification } from '../../../shared/notification/toastNotification';
import { useLocation } from 'react-router-dom';

type Props = {
    readonly authentication: AuthenticationState;
    readonly bulkChangeSubscriptionPeriodMutation: UseMutationResult<void, ErrorResponse, SubscriptionPeriodType>;
    readonly cancelSelection: () => void;
    readonly copyListToCart: (performChecks: boolean) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly hasActiveFilter: boolean;
    readonly isAdmin: boolean;
    readonly isInSelectionMode: boolean;
    readonly onBulkAddToGroup: (productGroupId: number) => void;
    readonly onBulkCopyToCart: (performChecks: boolean) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onChangePrice: (priceTier: PriceTier, performChecks: boolean) => Promise<BulkActionResponse>;
    readonly onCopyAmountChange: (
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) => Promise<BulkActionResponse>;
    readonly onCopyToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onDelete: () => void;
    readonly onDownloadCSV: () => void;
    readonly onDownloadPdf: () => void;
    readonly onDuplicateList: ({ sourceListName, t }: { sourceListName: string; t: TFunction }) => void;
    readonly onMoveToList: (
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onRemoveFromList: () => void;
    readonly onSaveSelectionlistName: UseMutateFunction<void, ErrorResponse, SelectionListUpdateParams>;
    readonly productSelection: ProductSelection;
    readonly productsCount: number;
    readonly selectionList: SelectionListOut;
    readonly spoc: SpocState;
};

export const SelectionListHeader: FC<Props> = ({
    authentication,
    bulkChangeSubscriptionPeriodMutation,
    cancelSelection,
    copyListToCart,
    hasActiveFilter,
    isAdmin,
    isInSelectionMode,
    onBulkAddToGroup,
    onBulkCopyToCart,
    onChangePrice,
    onCopyAmountChange,
    onCopyToList,
    onDelete,
    onDownloadCSV,
    onDownloadPdf,
    onDuplicateList,
    onMoveToList,
    onRemoveFromList,
    onSaveSelectionlistName,
    productSelection,
    productsCount,
    selectionList,
    spoc
}) => {
    const selectionDetailListRef = useRef(null);
    const listNameRef = useRef<HTMLInputElement | null>(null);
    const listNameFormRef = useRef<any>(null);
    const spacerRef = useRef<any>(null);
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { toggleCommentsBar } = useCommentsBarContext();
    const { isSticky } = useStickyHeader();

    const onToggleCommentsBar = debounce(toggleCommentsBar, 1200, {
        leading: true,
        maxWait: 1200
    });

    const [showApproval, setShowApproval] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showIsbnDialog, setShowIsbnDialog] = useState(false);
    const [editListName, setEditListName] = useState(false);
    const [selectionListName, setSelectionListName] = useState<string | null>(null);
    const [maxSelectionListInputWidth, setMaxSelectionListInputWidth] = useState(50);
    const [maxSelectionListInputWidthIsInitalSet, setMaxSelectionListInputWidthIsInitalSet] = useState(false);
    const { mutate: visibilityChangeMutation } = useChangeVisibility();
    const [analyse, setAnalyse] = useState<AnalyseResponseOut | undefined>(undefined);

    const [isAnalyseOpen, setIsAnalyseOpen] = useState(false);
    const [isAnalyseLoading, setIsAnalyseLoading] = useState(true);

    const onAnalyseClicked = useCallback(() => setIsAnalyseOpen(prevState => !prevState), []);

    function handleShowApproval() {
        setShowApproval(true);
    }

    const {
        mutate: sendForApprovalMutation,
        errorMessage,
        isLoading: isSendingForApproval,
        resetError
    } = useSendForApproval(selectionList.id!, () => {
        showNotification(i18n.t('notifications.sendForApproval.success'));
        handleHideApproval();
    });

    function handleHideApproval() {
        if (errorMessage) {
            resetError();
        }
        setShowApproval(false);
    }

    const setMaxInputWidth = useCallback(() => {
        const spacerWidth = spacerRef.current?.offsetWidth ?? 0;
        const listNameWidth = listNameFormRef.current?.offsetWidth ?? 0;
        const maxSelectionListInputWidthValue = spacerWidth + listNameWidth - 100;

        if (maxSelectionListInputWidthValue !== maxSelectionListInputWidth) {
            setMaxSelectionListInputWidth(maxSelectionListInputWidthValue);
            setMaxSelectionListInputWidthIsInitalSet(true);
        }
    }, [maxSelectionListInputWidth]);

    const handleResize = useCallback(() => {
        setMaxInputWidth();
    }, [setMaxInputWidth]);

    function handleShowIsbnDialog(state: boolean) {
        setShowIsbnDialog(state);
    }

    function handleShowConfirmation(state: boolean) {
        setShowConfirmation(state);
    }

    function startListNameEdit() {
        if (!editListName) {
            setEditListName(true);
            setSelectionListName(selectionList.listName ?? null);
        }
        listNameRef.current?.focus();
    }

    function cancelListNameEdit() {
        setEditListName(false);
        setSelectionListName(null);

        listNameRef.current?.blur();
    }

    function updateListName(listName: string) {
        setSelectionListName(listName);
    }

    const { isLoading, addByIsbnsToListBulk } = useAddByIsbnsToList();

    function triggerSaveSelectionlistName() {
        const { shared = false } = selectionList;
        const listName = selectionListName;

        if (listName && listName.length > 255) {
            globalWarning(t('notifications.updateSelectionListName.nameTooLong'));
            return;
        }
        if (listName && listName !== selectionList.listName) {
            onSaveSelectionlistName(
                { listName, shared },
                {
                    onSuccess: () => {
                        setEditListName(false);
                        listNameRef.current?.blur();
                    }
                }
            );
        } else {
            setEditListName(false);
            listNameRef.current?.blur();
        }
    }

    function changeVisibility(shared: boolean) {
        visibilityChangeMutation(
            { listId: selectionList.id!, shared, listName },
            {
                onSuccess: () => {
                    invalidateSelectionListSummary(selectionList.id!).then();
                }
            }
        );
    }

    const [addingToCart, setAddingToCart] = useState(false);

    useEffect(() => {
        setEditListName(false);
    }, [pathname]);

    const onAction = useCallback(
        (performChecks: boolean, options: CopyToCartOptions | IsbnAddToListOptions) => {
            if (isIsbnAddToListOptions(options)) {
                return addByIsbnsToListBulk({ selectionListId: options.listId, isbns: options.isbns, performChecks });
            }

            if (isCopyToCartOptions(options)) {
                return copyListToCart(performChecks);
            }
            throw new Error('technical error');
        },
        [addByIsbnsToListBulk, copyListToCart]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        CopyToCartOptions | IsbnAddToListOptions
    >({ onAction });

    const handleBulkCopyToCart = useCallback(async () => {
        setAddingToCart(true);
        try {
            await changePrice({ target: ListActionTarget.Cart, action: ACTION_COPY });
        } finally {
            setAddingToCart(false);
        }
    }, [changePrice]);

    const onBulkImportISBNs = useCallback(
        async (isbns: string) => {
            try {
                if (selectionList.id) {
                    await changePrice({
                        action: ACTION_ADD,
                        target: ListActionTarget.List,
                        listId: selectionList.id,
                        isbns
                    });
                }
            } finally {
                setShowIsbnDialog(false);
            }
        },
        [changePrice, selectionList.id]
    );

    const selectionListHeadlineClasses = classNames('selection-detail-list', {
        'moved-up': isInSelectionMode
    });

    const listName = (editListName ? selectionListName : selectionList.listName) || '';

    useLayoutEffect(() => {
        const fetchAnalyse = async () => {
            if (selectionList.id && isAnalyseOpen) {
                setIsAnalyseLoading(true);
                try {
                    const res = await fetchSelectionListAnalyse(selectionList.id);
                    setAnalyse(res);
                } catch (e) {
                    setAnalyse(undefined);
                } finally {
                    setIsAnalyseLoading(false);
                }
            }
        };

        fetchAnalyse().then();
    }, [isAnalyseOpen, selectionList.id]);

    useEffect(() => {
        setMaxInputWidth();
        window.addEventListener('resize', handleResize);
        if (maxSelectionListInputWidthIsInitalSet) {
            return;
        }
        setMaxInputWidth();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize, maxSelectionListInputWidthIsInitalSet, setMaxInputWidth]);

    // TODO check, if we really need the following effect
    useEffect(() => {
        // reset orderlist name input, when orderlist changes
        setSelectionListName(null);
    }, [selectionList.id]);

    return (
        <>
            <ConfirmationModalLayout
                visible={showConfirmation}
                title={t('selectionList.deleteList', {
                    listName: selectionList.listName
                })}
                text={t('selectionList.deleteMessage', {
                    listName: selectionList.listName
                })}
                onConfirm={onDelete}
                onCancel={() => handleShowConfirmation(false)}
            />
            <SendForApprovalDialog
                sharedSelectionlist={selectionList.shared}
                show={showApproval}
                errorMessage={errorMessage}
                pendingApprovalRequest={isSendingForApproval}
                sendForApproval={sendForApprovalMutation}
                hide={handleHideApproval}
            />
            <ImportIsbnDialog
                show={showIsbnDialog}
                onCancel={() => handleShowIsbnDialog(false)}
                onImport={onBulkImportISBNs}
                isLoading={isLoading}
            />
            <SelectionBulkContainer
                movedUp={isInSelectionMode}
                isSticky={isSticky}
                headerComponent={
                    <StSelectionListHeader ref={selectionDetailListRef} className={selectionListHeadlineClasses}>
                        <StBackButton onClick={() => window.history.back()}>
                            <i className="material-icons" style={{ fontSize: 'inherit' }}>
                                arrow_back
                            </i>
                        </StBackButton>
                        <StSelectionListIcon className="material-icons">content_paste</StSelectionListIcon>
                        <StListName>
                            <StListNameForm ref={listNameFormRef} className="list-name-form" active={editListName}>
                                <StNameInput
                                    ref={listNameRef}
                                    value={listName}
                                    minWidthInPx={50}
                                    onClick={startListNameEdit}
                                    onChange={updateListName}
                                    onEnterKey={triggerSaveSelectionlistName}
                                    onEscKey={cancelListNameEdit}
                                    maxWidthInPx={maxSelectionListInputWidth}
                                    truncated={!editListName}
                                    onFocus={startListNameEdit}
                                />
                                <StCheck
                                    data-testid="rename-list"
                                    onClick={triggerSaveSelectionlistName}
                                    className="material-icons"
                                    active={editListName}
                                >
                                    check
                                </StCheck>
                            </StListNameForm>
                        </StListName>
                        <StCommentsButton onClick={onToggleCommentsBar} title="Show/hide comments">
                            <i className="material-icons">comment</i>
                        </StCommentsButton>
                        <AnalyseDropDown
                            analysis={analyse}
                            tableComponent={AnalyseTable}
                            onAnalyseClicked={onAnalyseClicked}
                            title="Analyse list"
                            showDropdown={isAnalyseOpen}
                            isLoading={isAnalyseLoading}
                        />
                        <StPrivacyButton selectionList={selectionList} changeVisibility={changeVisibility} />
                        {
                            <StMoreButton
                                className="default-dd-nav expanded-dd list-actions fl-none centered"
                                title="more actions..."
                            >
                                <a data-toggle-dropdown="true" className="bt-dd">
                                    <i className="material-icons">more_horiz</i>
                                </a>
                                <div data-close-dropdown="true" className="default-dd">
                                    <span className="active-arrow" />
                                    <ul>
                                        <li>
                                            <a onClick={startListNameEdit} className="display-block">
                                                <i className="material-icons normal fl-left">mode_edit</i>{' '}
                                                {t('selectionList.moreButton.rename')}
                                            </a>
                                        </li>
                                        {productsCount <= MAX_SELECT ? (
                                            <li>
                                                <a
                                                    onClick={() => onDuplicateList({ sourceListName: listName, t })}
                                                    className="display-block"
                                                >
                                                    <i className="material-icons normal fl-left">content_copy</i>{' '}
                                                    {t('selectionList.moreButton.clone')}
                                                </a>
                                            </li>
                                        ) : null}
                                        <li>
                                            <a onClick={() => handleShowConfirmation(true)} className="display-block">
                                                <i className="material-icons normal fl-left">delete</i>{' '}
                                                {t('selectionList.moreButton.delete')}
                                            </a>
                                        </li>
                                        {isAdmin ? (
                                            <li>
                                                <a
                                                    onClick={() => handleShowIsbnDialog(true)}
                                                    className="display-block isbn-import"
                                                >
                                                    <i className="bolinda-icons fl-left">bolinda_only</i>{' '}
                                                    {t('selectionList.moreButton.import')}
                                                </a>
                                            </li>
                                        ) : null}
                                    </ul>
                                </div>
                            </StMoreButton>
                        }
                        <div ref={spacerRef} style={{ flexGrow: 1 }} />
                        <TotalAmountBadges {...selectionList} />
                        <StTotalDropDown
                            summary={selectionList}
                            spoc={spoc}
                            className="fl-none"
                            textTotalLine={t('components.totalDropdown.listTotal')}
                        />
                        {!Utils.isUserAllowedToOrder(authentication) ? (
                            <StButtonWithSpace
                                onClick={handleShowApproval}
                                size={ButtonSize.LARGE}
                                text={t('reserveReports.reserveReport.viewReport.sendForApproval')}
                                disabled={productsCount === 0}
                            />
                        ) : null}
                        {Utils.isUserAdmin(authentication) ? (
                            <>
                                <StButtonWithSpace
                                    onClick={onDownloadPdf}
                                    icon={MATERIAL_ICON_DOWNLOAD}
                                    size={ButtonSize.LARGE}
                                    text={t('downloadPdf')}
                                />
                                <StButtonWithSpace
                                    onClick={onDownloadCSV}
                                    icon={BOLINDA_ICON_BOLINDA_ONLY}
                                    size={ButtonSize.LARGE}
                                    text={t('downloadCsv')}
                                />
                            </>
                        ) : null}
                        <StButtonWithSpace
                            onClick={handleBulkCopyToCart}
                            icon={MATERIAL_ICON_CART}
                            size={ButtonSize.LARGE}
                            text={hasActiveFilter ? t('addToCart') : t('addCompleteListToCart')}
                            disabled={productsCount === 0 || addingToCart}
                            isLoading={addingToCart}
                        />
                    </StSelectionListHeader>
                }
                bulkMenuComponent={
                    <SelectionBulkMenu
                        isInSelectionMode={isInSelectionMode}
                        cancelSelection={cancelSelection}
                        selectedCount={getProductSelectionCount(productSelection)}
                        onBulkCopyToCart={onBulkCopyToCart}
                        onCopyToList={onCopyToList}
                        onMoveToList={onMoveToList}
                        onCopyAmountChange={onCopyAmountChange}
                        onRemoveFrom={onRemoveFromList}
                        onBulkAddToGroup={onBulkAddToGroup}
                        deleteLabel={t('deleteFromList')}
                        isAddToCartEnabled={false}
                        isMoveToListEnabled={true}
                        isDeleteFromEnabled={true}
                        isChangeQuantityEnabled={true}
                        isCopyToListEnabled={true}
                        isCopyToCartEnabled={true}
                        isAddToProductGroupEnabled={true}
                        isChangeSubscriptionPeriodEnabled={(selectionList.epressProductAmount ?? 0) > 0}
                        bulkChangeSubscriptionPeriodMutation={bulkChangeSubscriptionPeriodMutation}
                        onChangePrice={onChangePrice}
                        hideListId={selectionList.id}
                        isNothingSelected={nothingSelected(productSelection)}
                    />
                }
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};
